import { Field, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { InfoCircle } from 'react-bootstrap-icons';
import { TEXTAREA_ROW_LENGTH } from '../../../constants/constants';
import validCharacters from '../../../constants/valid-characters';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { useStore } from '../../../contexts/store-provider';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormTextArea from '../../../shared-components/FormikFormHandlers/FormikFormTextArea';
import { ProductValidationSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import Label from '../../../shared-components/label/label';
import { formatMessage } from '../../../translations/format-message';
import ProductStepper from './product-stepper';

interface IProps {
  id: number;
  data: any;
  onModalClosed: () => void;
  isLoading: boolean;
  next: (newData: any, final?: boolean) => void;
  validateProductName: (productId: string) => void;
  isProductNameValid: boolean;
}

const StepOne = (props: IProps) => {
  const FIELD_MAX_LENGTH = 256;
  const COMMENT_MAX_LENGTH = 500;
  const COMMENT_VALIDATION_LENGTH = COMMENT_MAX_LENGTH + 1;
  const { productStore } = useStore();
  const {
    setCopyProductDetails,
    ValidateProductNameService,
    resetValidateProductNameState,
  } = productStore;

  const [timer, setTimer] = useState<any>(null);
  const [isProductIdLimitReached, setIsProductIdLimitReached] =
    useState<boolean>(false);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false); // Default to false
  const [isSubmittingStep, setIsSubmittingStep] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      if (timer) clearTimeout(timer);
      resetValidateProductNameState();
    };
  }, [timer]);

  const handleSubmit = async (values: any) => {
    if (isSubmittingStep) {
      return;
    }
    setIsSubmittingStep(true);

    try {
      if (props.id <= 0) {
        const isValid = await ValidateProductNameService(
          Number(getTenantIdFromLocalStorage()),
          values.ProductName
        );
        if (isValid) {
          return;
        }
      }
      // console.log('Step 1 Payload:', values);
      props.next(values);
    } finally {
      setIsSubmittingStep(false);
    }
  };

  const handleProductIdChange = (e: any) => {
    const value = e.target.value;
    setIsNextDisabled(false); // Enable Next button initially

    if (timer) clearTimeout(timer);

    if (value && props.id <= 0 && /^[0-9]+$/.test(value)) {
      const newTimer = setTimeout(async () => {
        const isValid = await ValidateProductNameService(
          Number(getTenantIdFromLocalStorage()),
          value
        );
        setIsNextDisabled(isValid); // Disable Next button if ID exists
      }, 500);
      setTimer(newTimer);
    } else {
      setIsNextDisabled(true); // Disable if input is invalid
    }
  };

  return (
    <Formik
      initialValues={props.data}
      onSubmit={handleSubmit}
      validationSchema={ProductValidationSchema}
      validateOnBlur={false}
      enableReinitialize
    >
      {({ errors, handleSubmit, handleChange, touched, values }) => {
        const hasRemarksValidationError = Boolean(
          touched.Remarks && errors.Remarks
        );

        return (
          <Form
            id="formBody"
            onSubmit={handleSubmit}
          >
            <Modal.Body className="overflow-hidden">
              <ProductStepper currentStep={1} />
              <div className="row px-4">
                <div className="col-md-6 col-12">
                  <div className="form-group formSet">
                    <div className="d-flex align-items-center">
                      <Label
                        className="label-style col-form-label"
                        required={true}
                        label="product_id"
                      />
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {formatMessage('only_numbers_are_allowed')}
                          </Tooltip>
                        }
                      >
                        <span className="normal-info-svg ms-1">
                          <InfoCircle size={14} />
                        </span>
                      </OverlayTrigger>
                    </div>
                    <Field
                      data-testid="testProductId"
                      className="form-control"
                      name="ProductName"
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder=""
                      as={FormikFormInput}
                      validcharacter={validCharacters}
                      disabled={props.id > 0}
                      onChange={(e: any) => {
                        const rawValue = e.target.value || '';
                        const digitsOnly = rawValue.replace(/\D/g, '');
                        const trimmedValue = digitsOnly.slice(0, 10);

                        setIsProductIdLimitReached(trimmedValue.length >= 10);
                        e.target.value = trimmedValue;
                        handleChange(e);
                        handleProductIdChange(e);
                      }}
                      onBlur={(e: any) => {
                        setIsProductIdLimitReached(false);
                      }}
                    />
                    {isProductIdLimitReached ? (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_product_name')}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6 col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      label="katashiki"
                    />
                    <Field
                      data-testid="testProductId"
                      className="form-control"
                      name="Katashiki"
                      type="text"
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={FIELD_MAX_LENGTH}
                      validcharacter={validCharacters}
                    />
                    {String(values.Katashiki || '').length >=
                      FIELD_MAX_LENGTH && (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_limit_256')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-6 col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      label="customer_id"
                    />
                    <Field
                      data-testid="testCustomer"
                      className="form-control"
                      name="Customer"
                      type="text"
                      defaultValue={undefined}
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={FIELD_MAX_LENGTH}
                      validcharacter={validCharacters}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    />
                    {String(values.Customer || '').length >=
                      FIELD_MAX_LENGTH && (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_limit_256')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-6 col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      label="tray_size"
                    />
                    <Field
                      data-testid="testTraySize"
                      className="form-control"
                      name="TraySize"
                      type="text"
                      defaultValue={undefined}
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={FIELD_MAX_LENGTH}
                      validcharacter={validCharacters}
                    />
                    {String(values.TraySize || '').length >=
                      FIELD_MAX_LENGTH && (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_limit_256')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-4 col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      label="key_info_1"
                    />
                    <Field
                      data-testid="keyInfo1"
                      className="form-control"
                      name="KeyInfo1"
                      type="text"
                      defaultValue={undefined}
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={FIELD_MAX_LENGTH}
                      validcharacter={validCharacters}
                    />
                    {String(values.KeyInfo1 || '').length >=
                      FIELD_MAX_LENGTH && (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_limit_256')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-4 col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      label="key_info_2"
                    />
                    <Field
                      data-testid="keyInfo2"
                      className="form-control"
                      name="KeyInfo2"
                      type="text"
                      defaultValue={undefined}
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={FIELD_MAX_LENGTH}
                      validcharacter={validCharacters}
                    />
                    {String(values.KeyInfo2 || '').length >=
                      FIELD_MAX_LENGTH && (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_limit_256')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-md-4 col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      label="key_info_3"
                    />
                    <Field
                      data-testid="keyInfo3"
                      className="form-control"
                      name="KeyInfo3"
                      type="text"
                      defaultValue={undefined}
                      placeholder=""
                      as={FormikFormInput}
                      maxLength={FIELD_MAX_LENGTH}
                      validcharacter={validCharacters}
                    />
                    {String(values.KeyInfo3 || '').length >=
                      FIELD_MAX_LENGTH && (
                      <span className="text-danger errorMessage">
                        {formatMessage('max_char_limit_256')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-group formSet">
                    <Label
                      className="label-style col-form-label"
                      required={false}
                      label="Comment"
                    />
                    <Field
                      rows={TEXTAREA_ROW_LENGTH}
                      id="Comment"
                      className="form-control textAraProduct"
                      name="Remarks"
                      placeholder=""
                      type="text"
                      as={FormikFormTextArea}
                      maxLength={COMMENT_VALIDATION_LENGTH}
                    />
                    {String(values.Remarks || '').length > COMMENT_MAX_LENGTH &&
                      !hasRemarksValidationError && (
                        <span className="text-danger errorMessage">
                          {formatMessage('max_char_limit_500')}
                        </span>
                      )}
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="d-flex justify-content-center">
                <div className="row tab-action g-0">
                  <div className="col-12">
                    <Button
                      variant="light-danger"
                      type="button"
                      onClick={() => {
                        props.onModalClosed();
                      }}
                      disabled={props.isLoading}
                    >
                      <FormattedMessage id="cancel" />
                    </Button>
                    <LoaderButton
                      type="submit"
                      id="SaveTenant"
                      text="next"
                      disabled={
                        isNextDisabled || props.isLoading || isSubmittingStep
                      } // Bind to state
                    />
                  </div>
                </div>
              </div>
            </Modal.Footer>
          </Form>
        );
      }}
    </Formik>
  );
};
export default observer(StepOne);
